import { memo, useEffect, useState } from 'react';
import { BookCover } from './BookCover';
import { cn } from '@/lib/cn';
import type { BookView } from '@/types/api';

/**
 * BookCard — the repeating unit inside a BookCarousel. Fixed-width column
 * with the cover, a two-line-clamped title, and a one-line author. The
 * whole card is a button so the parent modal owner receives one `onSelect`
 * call per click; hover + focus rings surface through the `group` utilities.
 *
 * Dimensions: 240px wide card on >=sm (264px tall cover, a 240/264
 * aspect ratio), narrowing to 140px on mobile so ~2.5 cards peek through
 * instead of ~1.5. The cover height follows the card width via the fixed
 * aspect ratio. Sized so ~4.5 cards peek through at the AppShell's 1200px
 * content width. The placeholder cover stretches to the same box so the
 * layout doesn't jump when the image fails or is missing.
 *
 * Loading behaviour: the real-cover branch renders a subtle skeleton
 * behind the `<img>` and cross-fades to the loaded image. Without this,
 * covers in a carousel pop in in whatever order the browser decodes
 * them — which reads as jittery. The skeleton keeps every slot looking
 * "filled" until its image is actually ready.
 */
export interface BookCardProps {
  book: BookView;
  onSelect: (book: BookView) => void;
  className?: string;
}

function BookCardImpl({ book, onSelect, className }: BookCardProps) {
  const [coverLoaded, setCoverLoaded] = useState(false);

  // Reset the fade when the underlying book (or its cover URL) changes.
  // Also guards against the cached-image race by re-checking .complete
  // on the mounted node — if the browser already has it, `onLoad` may
  // not fire, so we resync from the DOM.
  useEffect(() => {
    setCoverLoaded(false);
  }, [book.bookCover]);

  const handleImgRef = (el: HTMLImageElement | null) => {
    if (el && el.complete && el.naturalWidth > 0) {
      setCoverLoaded(true);
    }
  };

  return (
    <button
      type="button"
      onClick={() => onSelect(book)}
      className={cn(
        'group flex w-[140px] flex-shrink-0 flex-col gap-2 text-left sm:w-[240px]',
        'rounded-2xl outline-none transition focus-visible:ring-2 focus-visible:ring-or-orange-400 focus-visible:ring-offset-2',
        className,
      )}
      aria-label={`Open ${book.title}${book.author ? ` by ${book.author}` : ''}`}
    >
      <div className="relative w-full overflow-hidden rounded-xl">
        {book.bookCover ? (
          // Real cover: crop from the top via object-top so spine copy stays
          // visible when the source image is taller than 264px. The skeleton
          // layer behind it fades out once the image has actually loaded.
          <div className="relative aspect-[240/264] w-full overflow-hidden rounded-xl bg-or-slate-100 shadow-or-card transition group-hover:-translate-y-0.5 group-hover:shadow-or-pop">
            <div
              aria-hidden
              className={cn(
                'absolute inset-0 animate-pulse bg-or-slate-200 transition-opacity duration-500',
                coverLoaded ? 'opacity-0' : 'opacity-100',
              )}
            />
            <img
              ref={handleImgRef}
              src={book.bookCover}
              alt={book.title}
              width={240}
              height={264}
              loading="lazy"
              onLoad={() => setCoverLoaded(true)}
              onError={() => setCoverLoaded(true)}
              className={cn(
                'absolute inset-0 h-full w-full object-cover object-top transition-opacity duration-500',
                coverLoaded ? 'opacity-100' : 'opacity-0',
              )}
            />
          </div>
        ) : (
          <div className="aspect-[240/264] w-full transition group-hover:-translate-y-0.5 group-hover:shadow-or-pop">
            <BookCover
              title={book.title}
              author={book.author}
              seed={book.id}
              w={240}
              h={264}
              className="h-full! w-full!"
            />
          </div>
        )}
      </div>
      <div className="px-1">
        <div className="font-display text-[14px] font-bold leading-tight text-or-slate-900 line-clamp-2">
          {book.title}
        </div>
        {book.author ? (
          <div className="mt-0.5 truncate text-[12px] text-or-slate-600">{book.author}</div>
        ) : null}
        <BookExtensions book={book} />
      </div>
    </button>
  );
}

export const BookCard = memo(BookCardImpl);

/**
 * Pill row rendered below the author indicating which supplementary
 * content the book offers:
 *   * "quiz"      — `book.hasQuiz` (live quiz only).
 *   * "resources" — activities (`book.hasResources`, one or more
 *     `book_activities` rows) AND/OR a vocabulary bookmark
 *     (`book.vocabularyBookmark`). Collapsed into a single pill.
 *
 * Hidden entirely when none apply so plain tiles stay uncluttered.
 */
function BookExtensions({ book }: { book: BookView }) {
  const showResources = book.hasResources || !!book.vocabularyBookmark;
  if (!book.hasQuiz && !showResources) {
    return null;
  }
  const pillClass =
    'inline-flex items-center rounded-full bg-or-slate-100 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-or-slate-700';
  return (
    <div className="mt-1.5 flex flex-wrap items-center gap-1" aria-label="Book extensions">
      {book.hasQuiz ? <span className={pillClass}>Quiz</span> : null}
      {showResources ? <span className={pillClass}>Resources</span> : null}
    </div>
  );
}
